import { BigNumber } from 'bignumber.js';

import {
  createFormatter,
  DEFAULT_LOCALE,
  FormatterFloatDigitsBigNumber,
} from './intl-formatter';

const formatterFractionalCache: Record<string, Intl.NumberFormat> = {};

type IAmountWithLetters<T extends number | BigNumber> = {
  newAmount: T;
  letter: string;
  formatted: string;
};

export const formatAmount = (
  amount = 0,
  round = true,
  decimalPlaces: number | 'auto' = 'auto'
) => {
  const defaultDecimalPlaces = round ? 0 : 2;
  decimalPlaces =
    decimalPlaces === 'auto' ? defaultDecimalPlaces : decimalPlaces;

  return createFormatter(false, round, decimalPlaces).format(amount);
};

export const formatAmountWithLetters = <T extends number | BigNumber>(
  amount: T,
  round: boolean | 'auto' = 'auto',
  decimalPlaces = 2
): IAmountWithLetters<T> => {
  let letter = '';
  let newAmount = amount;

  if (amount instanceof BigNumber) {
    if (amount.div(1000000).gte(1)) {
      newAmount = amount.div(1000000) as T;
      letter = 'M';
    } else if (amount.div(1000).gte(1)) {
      newAmount = amount.div(1000) as T;
      letter = 'K';
    }

    if (round && newAmount instanceof BigNumber) {
      newAmount = new BigNumber(
        newAmount.toFormat(round === 'auto' ? decimalPlaces : 0)
      ) as T;
    }
  } else if (typeof amount === 'number') {
    if (amount / 1000000 >= 1) {
      newAmount = (amount / 1000000) as T;
      letter = 'M';
    } else if (amount / 1000 >= 1) {
      newAmount = (amount / 1000) as T;
      letter = 'K';
    }

    if (round && typeof newAmount === 'number') {
      newAmount = Number(
        round === 'auto'
          ? createFormatter(false, false, decimalPlaces).format(newAmount)
          : createFormatter(false, true).format(newAmount)
      ) as T;
    }
  }

  return {
    newAmount,
    letter,
    formatted: `${newAmount}${letter}`,
  };
};

export const formatDecimalPercent = <T extends number | BigNumber>(
  value?: T,
  withSymbol = true,
  decimalPlaces = 2
): string | T => {
  let percent: T;

  if (value instanceof BigNumber) {
    percent = new BigNumber(
      value.multipliedBy(100).toFormat(decimalPlaces, {
        ...FormatterFloatDigitsBigNumber,
        groupSeparator: '',
      })
    ) as T;
  } else if (typeof value === 'number') {
    percent = Number(
      createFormatter(false, false, decimalPlaces, false).format(value * 100)
    ) as T;
  } else {
    return '';
  }

  const isPositive =
    value instanceof BigNumber
      ? value.isPositive() && !value.isZero()
      : value > 0;

  return withSymbol ? `${isPositive ? '+' : ''}${percent}%` : percent;
};

/**
 * Formats a number with up to the specified decimal places, stripping trailing zeros.
 * Useful for displaying fractional shares, tokens, or other quantities where
 * precision matters but unnecessary trailing zeros should be avoided.
 *
 * @example
 * formatFractionalAmount(1.5)        // "1.5"
 * formatFractionalAmount(1.500000)   // "1.5"
 * formatFractionalAmount(1.123456789) // "1.123457" (rounded to 6 decimals)
 * formatFractionalAmount(1234.5)     // "1,234.5"
 * formatFractionalAmount(1234.5, 6, false) // "1234.5" (no grouping)
 *
 * @param amount - The number to format
 * @param maxDecimals - Maximum decimal places to show (default: 6)
 * @param useGrouping - Whether to include thousand separators (default: true)
 * @returns Formatted string representation
 */
export const formatFractionalAmount = (
  amount: number,
  maxDecimals = 6,
  useGrouping = true
): string => {
  const cacheKey = `fractional_${maxDecimals}_${useGrouping}`;

  const formatter =
    formatterFractionalCache[cacheKey] ??
    (formatterFractionalCache[cacheKey] = new Intl.NumberFormat(
      DEFAULT_LOCALE,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxDecimals,
        useGrouping,
      }
    ));

  return formatter.format(amount);
};

/**
 * Formats a crypto amount with its ticker after the number, keeping up to the
 * asset's precision and stripping trailing zeros (see `formatFractionalAmount`).
 *
 * @example
 * formatCryptoAmount(0.001, 'BTC')          // "0.001 BTC"
 * formatCryptoAmount(1500.5, 'ETH', 18)     // "1,500.5 ETH"
 * formatCryptoAmount(1.23456, 'USDC', 2)    // "1.23 USDC"
 *
 * @param amount - The amount in the asset's main unit
 * @param symbol - The asset's ticker, e.g. "BTC"
 * @param maxDecimals - The asset's precision (default: 8, as for BTC)
 */
export const formatCryptoAmount = (
  amount: number,
  symbol: string,
  maxDecimals = 8
): string => `${formatFractionalAmount(amount, maxDecimals)} ${symbol}`;
